'use client';

import { useState, useCallback, useEffect } from 'react';
import type { RecentSearch } from '../_components/SearchBar';

// Parallel store to SearchBar's `localStorage['searches']`.
// SearchBar writes to BOTH keys; this hook reads from `bip_hotel_recent_searches`
// so the home-page "Your recent searches" section can render independently from
// the search-panel suggestion list. Same data shape (RecentSearch) — once we
// decide which store to keep, drop the other write site.

const KEY = 'bip_hotel_recent_searches';
const LEGACY_KEY = 'searches'; // SearchBar's existing CMS-format store
const MAX = 6;

function isSameSearch(a: RecentSearch, b: RecentSearch): boolean {
  return (
    a.dest_id === b.dest_id &&
    a.checkin === b.checkin &&
    a.checkout === b.checkout &&
    String(a.rooms) === String(b.rooms) &&
    a.adults === b.adults &&
    a.children === b.children &&
    a.ratecode === b.ratecode
  );
}

function isValidRecentSearch(e: unknown): e is RecentSearch {
  if (!e || typeof e !== 'object') return false;
  const v = e as Record<string, unknown>;
  return typeof v.dest_id === 'string' && typeof v.checkin === 'string' && typeof v.checkout === 'string';
}

function readKey(key: string): RecentSearch[] {
  try {
    const raw = localStorage.getItem(key);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed.filter(isValidRecentSearch) : [];
  } catch {
    return [];
  }
}

// Reads BOTH localStorage keys (`bip_hotel_recent_searches` + `searches`) and
// returns a dedup-merged list. Either store can populate the home section, so
// the user sees their data regardless of which write-site fired (or pre-existed
// from the legacy CMS shape).
function readStorage(): RecentSearch[] {
  if (typeof window === 'undefined') return [];
  const merged: RecentSearch[] = [];
  for (const entry of [...readKey(KEY), ...readKey(LEGACY_KEY)]) {
    if (!merged.some((m) => isSameSearch(m, entry))) merged.push(entry);
  }
  return merged.slice(0, MAX);
}

function writeStorage(items: RecentSearch[]) {
  if (typeof window === 'undefined') return;
  try {
    localStorage.setItem(KEY, JSON.stringify(items));
  } catch {
    // Quota — drop the oldest half and retry once before giving up.
    try {
      localStorage.setItem(KEY, JSON.stringify(items.slice(0, Math.ceil(items.length / 2))));
    } catch {
      try { localStorage.removeItem(KEY); } catch { /* ignore */ }
    }
  }
}

// Delete an entry from BOTH the new store and the legacy `searches` store so
// removal from the home-section UI doesn't get resurrected by the merged read.
function removeFromBothStores(predicate: (r: RecentSearch) => boolean) {
  if (typeof window === 'undefined') return;
  for (const k of [KEY, LEGACY_KEY]) {
    try {
      const list = readKey(k).filter(predicate);
      localStorage.setItem(k, JSON.stringify(list));
    } catch { /* ignore */ }
  }
}

export function addRecentSearch(entry: RecentSearch): void {
  const list = readStorage().filter((r) => !isSameSearch(r, entry));
  list.unshift(entry);
  writeStorage(list.slice(0, MAX));
}

export function useRecentSearches() {
  const [searches, setSearches] = useState<RecentSearch[]>([]);
  const [hydrated, setHydrated] = useState(false);

  useEffect(() => {
    setSearches(readStorage());
    setHydrated(true);
  }, []);

  const add = useCallback((entry: RecentSearch) => {
    setSearches((prev) => {
      const deduped = prev.filter((r) => !isSameSearch(r, entry));
      const next = [entry, ...deduped].slice(0, MAX);
      writeStorage(next);
      return next;
    });
  }, []);

  const remove = useCallback((entry: RecentSearch) => {
    setSearches((prev) => prev.filter((r) => !isSameSearch(r, entry)));
    removeFromBothStores((r) => !isSameSearch(r, entry));
  }, []);

  const clear = useCallback(() => {
    setSearches([]);
    if (typeof window === 'undefined') return;
    try { localStorage.removeItem(KEY); } catch { /* ignore */ }
    try { localStorage.removeItem(LEGACY_KEY); } catch { /* ignore */ }
  }, []);

  return { searches, add, remove, clear, hydrated };
}
